import React, { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'cap:state:v1';

async function loadState() {
  try {
    const res = await window.storage.get(STORAGE_KEY);
    return res ? JSON.parse(res.value) : null;
  } catch {
    return null;
  }
}

async function saveState(state) {
  try {
    await window.storage.set(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('save failed', e);
  }
}

const SYMPTOMS = [
  { id: 'fog', label: 'Brain fog', sub: 'Thinking through treacle' },
  { id: 'focus', label: 'Difficulty concentrating', sub: 'Hard to stay on task' },
  { id: 'memory', label: 'Memory lapses', sub: 'Forgetting words, names, why you went somewhere' },
  { id: 'fatigue', label: 'Mental fatigue', sub: 'Exhausted before the day is over' },
  { id: 'decisions', label: 'Decision paralysis', sub: 'Everything feels too hard to choose between' },
  { id: 'overwhelm', label: 'Overwhelm', sub: 'Too much, not enough capacity' },
  { id: 'slow', label: 'Slow processing', sub: 'Taking longer than usual to read or respond' },
  { id: 'anxiety', label: 'Anxiety', sub: "Worry, hyper-vigilance that won't settle" },
  { id: 'emotion', label: 'Emotional dysregulation', sub: 'Reactions feel bigger than usual' },
  { id: 'confidence', label: 'Loss of confidence', sub: 'Doubting your own ability' },
  { id: 'words', label: 'Word retrieval difficulties', sub: "Reaching for a word that isn't there" },
  { id: 'avoidance', label: 'Avoidance', sub: 'Putting things off because they feel too heavy' },
];

const CAUSES = [
  { id: 'menopause', label: 'Perimenopause or menopause' },
  { id: 'adhd', label: 'ADHD or other neurodivergent traits' },
  { id: 'stress', label: 'Chronic stress or burnout' },
  { id: 'sleep', label: 'Poor or disrupted sleep' },
  { id: 'covid', label: 'Long COVID' },
  { id: 'cancer', label: 'Cancer treatment' },
  { id: 'caring', label: 'Caring responsibilities' },
  { id: 'parenting', label: 'Parenting young children' },
  { id: 'chronicpain', label: 'Chronic pain or illness' },
  { id: 'mentalhealth', label: 'Anxiety or depression' },
  { id: 'overload', label: 'Information overload' },
  { id: 'trauma', label: 'Trauma or PTSD' },
  { id: 'grief', label: 'Bereavement' },
  { id: 'isolation', label: 'Isolation' },
  { id: 'ai', label: 'Heavy AI use' },
  { id: 'other', label: 'Something else' },
];

const DRAINS = [
  'Multitasking — switching between tasks constantly',
  'Back-to-back meetings with no breaks',
  'Phone from wake-up to bedtime',
  'Social media scrolling, especially at night',
  'Poor or disrupted sleep (under 7 hours)',
  'Sitting at a desk for hours without movement',
  'No end to the working day — always on',
  'Information overload — too much to process',
  'Negative or high-conflict relationships at work',
  'Suppressing emotions or masking at work',
  'Finishing the day with nothing left for yourself',
  'Delegating thinking to AI tools',
];

const CONDITIONS = [
  {
    id: 'menopause', icon: '🌿', color: '#1A6B6B',
    title: 'Perimenopause and menopause',
    sub: 'Hormonal brain transition affecting focus, memory, processing, sleep, mood',
    whats: 'Oestrogen receptors are dense in the hippocampus and prefrontal cortex — the regions for memory and decision-making. As oestrogen fluctuates, these systems are directly affected. Cognitive performance generally stays within normal range, but it takes more effort. That extra effort is real and tiring.',
    helps: [
      'Treat sleep as the primary intervention — disrupted sleep amplifies every cognitive symptom',
      'Externalise memory ruthlessly: written notes, double reminders, visible lists',
      'Protect your peak cognitive window for the most demanding work',
      'Speak to a GP about HRT — oestrogen therapy near menopause onset is linked to improved verbal memory',
      'Brain fog is not dementia — it fluctuates, ties to hormonal change, and typically improves post-menopause',
      'Reduce the load: fewer commitments, written follow-ups, flexibility on timing where possible',
    ],
  },
  {
    id: 'adhd', icon: '⚡', color: '#5C3060',
    title: 'ADHD and neurodivergent traits',
    sub: 'Interest-based attention, executive function, rejection sensitivity, masking',
    whats: 'ADHD is not a focus problem — it is an interest-based, state-dependent attention system. The prefrontal cortex works differently; executive function requires more scaffolding. Masking takes significant cognitive energy that is unavailable for thinking. In perimenopause, oestrogen drop strips away coping strategies built over decades.',
    helps: [
      'Body-doubling — working alongside another person anchors attention for many ADHD brains',
      'Short, intense sprints with genuine breaks — not long sessions',
      'Reduce friction before starting: set up your environment and single task in advance',
      'External scaffolding: double alarms, visual calendars, single-capture systems — this is strategy, not laziness',
      'Movement before demanding cognitive tasks — 20 minutes of exercise improves focus significantly',
      'If not yet assessed, a proper ADHD assessment is worth pursuing',
    ],
  },
  {
    id: 'stress', icon: '🔥', color: '#2E4A5E',
    title: 'Chronic stress and burnout',
    sub: 'High cortisol, depleted executive function, emotional dysregulation, exhaustion',
    whats: 'Chronic stress floods the system with cortisol, which impairs the prefrontal cortex while amplifying the amygdala. Decisions feel harder, emotions feel bigger, and the brain is simultaneously exhausted and unable to rest. Burnout is the end stage: the tank is empty and normal refuelling no longer works.',
    helps: [
      'Identify and name your top cognitive drains — then address one at a time',
      'The breath technique (6 in, 8 out) directly reduces cortisol — use it consistently, not just in crisis',
      'Genuine recovery: low-stimulation time, nature, movement. Not scrolling',
      'Reduce the decision load: simplify routines, batch decisions, cut non-essential commitments',
      'Single-task — multitasking when cortisol is high causes disproportionate cognitive damage',
      'Be honest about capacity at work — frame it as a quality and risk issue, not personal failure',
    ],
  },
  {
    id: 'caring', icon: '💛', color: '#7A3020',
    title: 'Caring responsibilities and parenting',
    sub: 'Cognitive overload, mental load, sleep debt, identity pressure, reduced recovery',
    whats: 'Caring — for children, elderly parents, or a person with illness — carries a hidden cognitive tax: the mental load of constant planning and anticipating needs. This is invisible to most organisations but real cognitive labour. Sleep disruption compounds everything. Two thirds of unpaid caring roles are held by women.',
    helps: [
      "Make the invisible visible: write down the mental load items you're carrying",
      'Sleep when you genuinely can — even short recovery windows matter',
      'Reduce unnecessary cognitive switching: batch similar tasks, build routines that run on autopilot',
      'Protect one small window of genuine recovery time daily — even 10 minutes',
      'Externalise everything possible: shared calendars, routines, checklists for recurring tasks',
      'Ask for flexibility on start times or working patterns where the option exists',
    ],
  },
  {
    id: 'covid', icon: '🦠', color: '#2E5E3A',
    title: 'Long COVID and post-viral illness',
    sub: 'Systemic inflammation, neurological impact, fatigue, post-exertional malaise',
    whats: 'Long COVID can cause direct neurological effects: brain fog, memory and concentration difficulties, slowed processing, disproportionate fatigue. Post-exertional malaise means cognitive effort can trigger symptom flares. Standard productivity strategies actively make things worse.',
    helps: [
      'Pacing is the core strategy — stay within your energy envelope and build gradually',
      'Cognitive tasks count as exertion — factor them into your daily energy budget',
      'Single-task, short sprints with genuine rest between',
      'Write everything down: memory and recall may be significantly impaired',
      'Rest that is genuinely restorative: lying down, quiet, minimal stimulation — not screens',
      'Work with your employer on reasonable adjustments: reduced demands, async communication, flexible timing',
    ],
  },
  {
    id: 'cancer', icon: '🎗', color: '#3A3A2A',
    title: 'Cancer treatment and recovery',
    sub: 'Chemotherapy fog, fatigue, processing speed, memory, emotional impact',
    whats: 'Chemotherapy-related cognitive impairment ("chemo brain") is a well-documented neurological effect, affecting memory, processing speed, attention, and word retrieval — sometimes persisting after treatment ends. Fatigue, stress, and the emotional weight of diagnosis compound the cognitive impact.',
    helps: [
      "Prioritise cognitive rest — your brain is doing significant repair work; protect its energy",
      'Write everything down: a single trusted capture system for tasks, appointments, decisions',
      'Protect your best cognitive window, even if shorter than it used to be',
      'Be explicit with colleagues about what helps: written follow-ups, advance notice, reduced decisions',
      'Gentle aerobic movement, where energy allows, supports neuroplasticity and recovery',
      'Neuroplasticity continues: recovery is possible, and structured cognitive strategy training helps',
    ],
  },
];

const HACKS = [
  {
    id: 'breath', tag: 'Brain Hack 01 — Anxiety & Overwhelm', title: 'The locus coeruleus breath',
    body: "A tiny structure in your brain releases noradrenaline — too much drives anxiety; too little leaves you flat. You can regulate it directly through breathing. This is not relaxation. It's a neurochemical intervention.",
    how: 'Breathe in through your nose for a count of 6. Breathe out slowly for a count of 8 — a long, controlled exhale. Repeat 3–4 times. Works before meetings, presentations, difficult conversations, or a brain fog moment. Silent. Invisible. Anywhere.',
  },
  {
    id: 'single', tag: 'Brain Hack 02 — Focus & Cognitive Load', title: 'Single-tasking',
    body: 'Multitasking is not efficient — it is costly. Every task switch has a reorientation cost. Tasks take longer, errors increase, and you end up more depleted.',
    how: 'One task. Notifications off. Timer for 15–25 minutes. Work on that one thing until the timer ends. Break. Repeat. The brain gets measurably better at this over weeks.',
  },
];

const TABS = [
  { id: 'measure', label: 'Measure', sub: 'Where am I now?' },
  { id: 'protect', label: 'Protect', sub: "What's depleting me?" },
  { id: 'strengthen', label: 'Strengthen', sub: 'Strategies that work' },
  { id: 'situations', label: 'Situations', sub: 'What helps when…' },
  { id: 'plan', label: 'My Plan', sub: "What I'm doing" },
];

const DEFAULT_STATE = {
  symptoms: {}, causes: {}, otherCause: '', betterLooksLike: '', peakWindow: '', fogWindow: '',
  scoreNow: 6, drainScores: {}, topDrains: '', sleepHours: '', sleepDisruptors: '', sleepChange: '',
  conditionNotes: {}, thisWeek: '', thisMonth: '', askAtWork: '', stopDoing: '',
  reviewScore: '', reviewImproved: '', reviewNeedsWork: '',
};

function TextArea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%', fontFamily: 'inherit', fontSize: 14, padding: '12px 14px',
        borderRadius: 8, border: '1px solid #D4E6E6', background: '#FAFEFE',
        color: '#1C2B2B', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5,
      }}
      onFocus={(e) => (e.target.style.borderColor = '#1A6B6B')}
      onBlur={(e) => (e.target.style.borderColor = '#D4E6E6')}
    />
  );
}

function TextInput({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', fontFamily: 'inherit', fontSize: 14, padding: '10px 14px',
        borderRadius: 8, border: '1px solid #D4E6E6', background: '#FAFEFE',
        color: '#1C2B2B', outline: 'none', boxSizing: 'border-box',
      }}
      onFocus={(e) => (e.target.style.borderColor = '#1A6B6B')}
      onBlur={(e) => (e.target.style.borderColor = '#D4E6E6')}
    />
  );
}

function CheckCard({ label, sub, checked, onToggle, accent = '#1A6B6B' }) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px',
        borderRadius: 8, border: `1px solid ${checked ? accent : '#D4E6E6'}`,
        background: checked ? `${accent}14` : '#F5F9F9', cursor: 'pointer',
        textAlign: 'left', width: '100%', transition: 'all 0.15s', fontFamily: 'inherit',
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: 4,
        border: `2px solid ${checked ? accent : '#B8D0D0'}`,
        background: checked ? accent : 'transparent', flexShrink: 0, marginTop: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && (
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
            <path d="M1 4.5L4 7.5L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#1C2B2B' }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: '#7A9494', marginTop: 1 }}>{sub}</div>}
      </div>
    </button>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
      color: '#1A6B6B', marginBottom: 8,
    }}>{children}</div>
  );
}

function Callout({ children, color = '#1A6B6B', bg = '#E4F2F2' }) {
  return (
    <div style={{
      background: bg, borderLeft: `3px solid ${color}`, borderRadius: '0 8px 8px 0',
      padding: '14px 18px', fontSize: 14, color: '#243030', lineHeight: 1.6,
    }}>{children}</div>
  );
}

function ScoreSlider({ value, onChange }) {
  return (
    <div>
      <input
        type="range" min={1} max={10} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: '#1A6B6B' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#7A9494', marginTop: 2 }}>
        <span>Very depleted</span>
        <span style={{ fontWeight: 700, color: '#1A6B6B', fontSize: 14 }}>{value}/10</span>
        <span>Functioning at my best</span>
      </div>
    </div>
  );
}

function BrainBattery({ pct, depletionCount }) {
  const fillColor = pct >= 70 ? '#1A6B6B' : pct >= 40 ? '#C8963E' : '#C0392B';
  const label = pct >= 70 ? 'Functioning well' : pct >= 40 ? 'Under strain' : 'Significantly depleted';
  return (
    <div style={{
      background: '#111C1C', borderRadius: 16, padding: '24px 28px',
      display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
    }}>
      <div style={{ position: 'relative', width: 64, height: 100, flexShrink: 0 }}>
        <svg width="64" height="100" viewBox="0 0 64 100">
          <rect x="20" y="0" width="24" height="8" rx="2" fill="#3A4A4A" />
          <rect x="2" y="8" width="60" height="90" rx="10" fill="none" stroke="#3A4A4A" strokeWidth="3" />
          <rect
            x="6" y={8 + 86 * (1 - pct / 100)} width="52"
            height={86 * (pct / 100)} rx="7" fill={fillColor}
            style={{ transition: 'height 0.5s ease, y 0.5s ease' }}
          />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#C8963E', marginBottom: 6 }}>
          Your cognitive battery, right now
        </div>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: 26, color: 'white', fontStyle: 'italic', marginBottom: 6 }}>
          {label}
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
          {depletionCount === 0
            ? "Tick what you're experiencing on the left to see where you stand."
            : `Based on ${depletionCount} symptom${depletionCount === 1 ? '' : 's'} you've flagged. This isn't a diagnosis — it's a starting point for what to protect first.`}
        </div>
      </div>
    </div>
  );
}

export default function CognitiveActionPlan() {
  const [state, setState] = useState(DEFAULT_STATE);
  const [tab, setTab] = useState('measure');
  const [loaded, setLoaded] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    loadState().then((s) => {
      if (s) setState({ ...DEFAULT_STATE, ...s });
      setLoaded(true);
    });
  }, []);

  const update = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      saveState(next);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 900);
      return next;
    });
  }, []);

  const toggleSet = (key, id) => {
    const current = { ...state[key] };
    current[id] = !current[id];
    update({ [key]: current });
  };

  if (!loaded) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, fontFamily: 'system-ui', color: '#7A9494' }}>
        Loading your plan…
      </div>
    );
  }

  const symptomCount = Object.values(state.symptoms).filter(Boolean).length;
  const battery = Math.max(8, 100 - symptomCount * 7);

  return (
    <div style={{
      fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: '#EEF5F5', minHeight: '100%', color: '#243030',
      maxWidth: 880, margin: '0 auto', borderRadius: 16, overflow: 'hidden',
      boxShadow: '0 2px 24px rgba(0,0,0,0.06)',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #0D1F1F 0%, #1A6B6B 70%, #2E8B8B 100%)',
        padding: '32px 32px 24px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -60, right: -60, width: 200, height: 200,
          borderRadius: '50%', background: 'rgba(255,255,255,0.04)',
        }} />
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#C8963E', marginBottom: 10 }}>
          Neuro-Informed Ltd
        </div>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 30, color: 'white', margin: 0, lineHeight: 1.15 }}>
          My Cognitive Action Plan
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginTop: 8, maxWidth: 460, lineHeight: 1.5 }}>
          A working plan for protecting and strengthening your cognitive health — menopause, neurodivergence, stress, caring, illness, or all of the above.
        </p>
        <div style={{
          position: 'absolute', top: 28, right: 28, fontSize: 11,
          color: savedFlash ? '#C8963E' : 'rgba(255,255,255,0.3)', transition: 'color 0.3s', fontWeight: 600,
        }}>
          {savedFlash ? '✓ Saved' : 'Autosaves as you type'}
        </div>
      </div>

      <div style={{ display: 'flex', background: 'white', borderBottom: '1px solid #D4E6E6', overflowX: 'auto' }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: '1 0 auto', minWidth: 110, padding: '14px 10px', textAlign: 'center',
              background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              borderBottom: tab === t.id ? '3px solid #1A6B6B' : '3px solid transparent',
              transition: 'border-color 0.15s',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: tab === t.id ? '#1A6B6B' : '#243030' }}>{t.label}</div>
            <div style={{ fontSize: 11, color: '#7A9494', marginTop: 1 }}>{t.sub}</div>
          </button>
        ))}
      </div>

      <div style={{ padding: '28px 32px 40px', background: 'white' }}>
        {tab === 'measure' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            <BrainBattery pct={battery} depletionCount={symptomCount} />
            <div>
              <SectionLabel>What I'm experiencing</SectionLabel>
              <p style={{ fontSize: 13, color: '#7A9494', marginTop: -4, marginBottom: 12 }}>
                Tick anything that feels relevant right now. All common. All addressable.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                {SYMPTOMS.map((s) => (
                  <CheckCard key={s.id} label={s.label} sub={s.sub} checked={!!state.symptoms[s.id]} onToggle={() => toggleSet('symptoms', s.id)} />
                ))}
              </div>
            </div>
            <div>
              <SectionLabel>What's contributing to this</SectionLabel>
              <p style={{ fontSize: 13, color: '#7A9494', marginTop: -4, marginBottom: 12 }}>
                More than one can be true — and often is.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                {CAUSES.map((c) => (
                  <CheckCard key={c.id} label={c.label} checked={!!state.causes[c.id]} onToggle={() => toggleSet('causes', c.id)} accent="#5C3060" />
                ))}
              </div>
              {state.causes.other && (
                <div style={{ marginTop: 10 }}>
                  <TextInput value={state.otherCause} onChange={(v) => update({ otherCause: v })} placeholder="What else is contributing?" />
                </div>
              )}
            </div>
            <div>
              <SectionLabel>Rate your cognitive health right now</SectionLabel>
              <ScoreSlider value={state.scoreNow} onChange={(v) => update({ scoreNow: v })} />
            </div>
            <div>
              <SectionLabel>What would a 7 or 8 out of 10 feel like for me?</SectionLabel>
              <TextArea value={state.betterLooksLike} onChange={(v) => update({ betterLooksLike: v })} placeholder="Be specific — what would you be doing, thinking, or feeling differently?" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <SectionLabel>When do I think most clearly?</SectionLabel>
                <TextInput value={state.peakWindow} onChange={(v) => update({ peakWindow: v })} placeholder="e.g. 9–11am" />
              </div>
              <div>
                <SectionLabel>When do I feel most foggy?</SectionLabel>
                <TextInput value={state.fogWindow} onChange={(v) => update({ fogWindow: v })} placeholder="e.g. mid-afternoon" />
              </div>
            </div>
          </div>
        )}

        {tab === 'protect' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            <div>
              <SectionLabel>What is depleting me</SectionLabel>
              <p style={{ fontSize: 13, color: '#7A9494', marginTop: -4, marginBottom: 14 }}>
                Score each from 1 (never an issue) to 5 (constant problem).
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {DRAINS.map((d, i) => {
                  const val = state.drainScores[i] || 0;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#F5F9F9', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ flex: 1, fontSize: 13.5, color: '#1C2B2B' }}>{d}</div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            onClick={() => update({ drainScores: { ...state.drainScores, [i]: n } })}
                            style={{
                              width: 26, height: 26, borderRadius: 6, fontSize: 11, fontWeight: 700,
                              border: '1px solid #D4E6E6', cursor: 'pointer', fontFamily: 'inherit',
                              background: val === n ? (n >= 4 ? '#C0392B' : n === 3 ? '#C8963E' : '#1A6B6B') : 'white',
                              color: val === n ? 'white' : '#7A9494', transition: 'all 0.1s',
                            }}
                          >{n}</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <SectionLabel>My top 3 cognitive drains right now</SectionLabel>
              <TextArea value={state.topDrains} onChange={(v) => update({ topDrains: v })} placeholder="Name them — the ones that matter most" rows={2} />
            </div>
            <div style={{ background: '#F5F9F9', borderRadius: 12, padding: '20px 22px', border: '1px solid #D4E6E6' }}>
              <SectionLabel>Sleep — the foundation</SectionLabel>
              <p style={{ fontSize: 13.5, color: '#4A5E5E', lineHeight: 1.6, marginTop: 0, marginBottom: 14 }}>
                Poor sleep is the single biggest amplifier of every cognitive symptom. All other strategies work better when sleep is protected.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1A6B6B', marginBottom: 4 }}>Average hours of sleep</div>
                  <TextInput value={state.sleepHours} onChange={(v) => update({ sleepHours: v })} placeholder="e.g. 6" />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1A6B6B', marginBottom: 4 }}>Main disruptors</div>
                  <TextInput value={state.sleepDisruptors} onChange={(v) => update({ sleepDisruptors: v })} placeholder="e.g. night sweats, scrolling" />
                </div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#1A6B6B', marginBottom: 4 }}>One sleep habit to change</div>
              <TextInput value={state.sleepChange} onChange={(v) => update({ sleepChange: v })} placeholder="Be specific" />
              <div style={{ marginTop: 16 }}>
                <Callout>
                  No phone in bed · consistent sleep and wake times · a screen-free wind-down · a cool room · magnesium glycinate · speak to a GP if night sweats are disrupting sleep
                </Callout>
              </div>
            </div>
          </div>
        )}

        {tab === 'strengthen' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {HACKS.map((h) => (
              <div key={h.id} style={{ background: 'linear-gradient(135deg, #111C1C 0%, #1A6B6B 100%)', borderRadius: 14, padding: '26px 28px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#C8963E', marginBottom: 10 }}>{h.tag}</div>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: 21, color: 'white', marginBottom: 10 }}>{h.title}</div>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, marginBottom: 12 }}>{h.body}</p>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.92)', lineHeight: 1.6, fontWeight: 500, margin: 0 }}>{h.how}</p>
              </div>
            ))}
            <div>
              <SectionLabel>Other strategies that help</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginTop: 10 }}>
                {[
                  { icon: '⏱', title: 'Movement & nature', body: 'Twenty minutes outdoors restores directed attention and reduces cortisol. No gym required.' },
                  { icon: '📚', title: 'Read for 10 minutes', body: "Builds sustained attention the way scrolling erodes it. Effortful at first — that's the training." },
                  { icon: '📋', title: 'Externalise memory', body: 'One trusted capture system. Written follow-ups after meetings. Double reminders for what matters.' },
                  { icon: '🌊', title: 'Name the emotion', body: '"Stressed" covers many states. Naming it precisely measurably reduces amygdala activation.' },
                ].map((s, i) => (
                  <div key={i} style={{ background: '#F5F9F9', border: '1px solid #D4E6E6', borderRadius: 10, padding: '16px 18px' }}>
                    <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1C2B2B', marginBottom: 4 }}>{s.title}</div>
                    <div style={{ fontSize: 13, color: '#4A5E5E', lineHeight: 1.5 }}>{s.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'situations' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <p style={{ fontSize: 13.5, color: '#4A5E5E', marginTop: 0 }}>
              The strategies are largely the same — but these notes speak directly to what shapes brain fog and overwhelm in each situation.
            </p>
            {CONDITIONS.map((c) => (
              <details key={c.id} style={{ border: '1px solid #D4E6E6', borderRadius: 12, overflow: 'hidden' }}>
                <summary style={{ cursor: 'pointer', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, background: c.color, color: 'white', listStyle: 'none' }}>
                  <span style={{ fontSize: 22 }}>{c.icon}</span>
                  <span>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{c.title}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>{c.sub}</div>
                  </span>
                </summary>
                <div style={{ padding: '18px 20px', background: 'white' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.color, marginBottom: 6 }}>What's happening</div>
                  <p style={{ fontSize: 13.5, color: '#4A5E5E', lineHeight: 1.6, marginTop: 0, marginBottom: 14 }}>{c.whats}</p>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.color, marginBottom: 6 }}>What helps most</div>
                  <ul style={{ margin: 0, paddingLeft: 18, marginBottom: 14 }}>
                    {c.helps.map((h, i) => (<li key={i} style={{ fontSize: 13.5, color: '#243030', lineHeight: 1.6, marginBottom: 4 }}>{h}</li>))}
                  </ul>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.color, marginBottom: 6 }}>My notes</div>
                  <TextArea value={state.conditionNotes[c.id] || ''} onChange={(v) => update({ conditionNotes: { ...state.conditionNotes, [c.id]: v } })} placeholder="What's most relevant to me right now..." rows={2} />
                </div>
              </details>
            ))}
          </div>
        )}

        {tab === 'plan' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <p style={{ fontSize: 13.5, color: '#4A5E5E', marginTop: 0 }}>
              Choose deliberately. Small consistent changes beat ambitious ones that don't last.
            </p>
            <div>
              <SectionLabel>This week</SectionLabel>
              <TextArea value={state.thisWeek} onChange={(v) => update({ thisWeek: v })} placeholder="One or two changes to start immediately" rows={2} />
            </div>
            <div>
              <SectionLabel>This month</SectionLabel>
              <TextArea value={state.thisMonth} onChange={(v) => update({ thisMonth: v })} placeholder="Habits to build over 4 weeks" rows={2} />
            </div>
            <div>
              <SectionLabel>What I want to ask for at work</SectionLabel>
              <TextArea value={state.askAtWork} onChange={(v) => update({ askAtWork: v })} placeholder="What adjustments or conversations would genuinely help?" />
            </div>
            <div>
              <SectionLabel>What I'm going to stop doing</SectionLabel>
              <TextArea value={state.stopDoing} onChange={(v) => update({ stopDoing: v })} placeholder="Sometimes protection is about removal, not addition" rows={2} />
            </div>
            <div style={{ background: '#F3EDF5', border: '1px solid #C9A8D0', borderRadius: 12, padding: '20px 22px' }}>
              <SectionLabel>4-week review</SectionLabel>
              <p style={{ fontSize: 13, color: '#5C3060', marginTop: 0, marginBottom: 14 }}>Come back to this after 4 weeks.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#5C3060', marginBottom: 4 }}>Score now (1–10)</div>
                  <TextInput value={state.reviewScore} onChange={(v) => update({ reviewScore: v })} placeholder="—" />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#5C3060', marginBottom: 4 }}>What improved?</div>
                  <TextInput value={state.reviewImproved} onChange={(v) => update({ reviewImproved: v })} placeholder="—" />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#5C3060', marginBottom: 4 }}>Still needs work?</div>
                  <TextInput value={state.reviewNeedsWork} onChange={(v) => update({ reviewNeedsWork: v })} placeholder="—" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ background: '#111C1C', padding: '20px 32px', textAlign: 'center' }}>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', margin: 0 }}>
          Your brain is trainable, at any age. 75% of people who complete structured brain health training show measurable cognitive gains.
        </p>
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 8 }}>
          Naomi Glover MSc FRSA FRSPH · Neuro-Informed Ltd · naomi@neuro-informed.com
        </p>
      </div>
    </div>
  );
}
